/**
 * RefundModal — оформление возврата и дозаполнение легаси-возвратов.
 *
 * Используется из попапа заказа (кнопка «Сделать возврат») и со страницы
 * /refunds (кнопка «Дозаполнить» у возвратов, перенесённых миграцией 0031).
 *
 * Полный/частичный определяет бэк: сумма сравнивается с оплаченной суммой
 * в той же валюте. Полный дополнительно переводит заказ в статус «Возврат».
 */
import React, { useEffect, useState } from 'react';
import client from '../api/client';
import {
  REFUND_REASONS,
  REFUND_CURRENCIES,
  fmtAmount,
} from '../utils/refunds';
import './RefundModal.css';

interface Props {
  userId: number;
  username: string;
  /** Для дозаполнения существующей строки. Без него — создание нового. */
  refundId?: number;
  initial?: {
    amount?: number | null;
    currency?: string;
    reason?: string | null;
    reasonText?: string;
  };
  onClose: () => void;
  /** Возврат сохранён. statusChanged = заказ переведён в «Возврат». */
  onDone: (res: { statusChanged?: boolean }) => void;
}

interface ExistingRefund {
  id: number;
  amount: number | null;
  currency: string;
  isFull: boolean;
}

interface UserRefundInfo {
  currency: string;
  paidByCurrency: Record<string, number>;
  refunds: ExistingRefund[];
}

const RefundModal: React.FC<Props> = ({
  userId,
  username,
  refundId,
  initial,
  onClose,
  onDone,
}) => {
  const [info, setInfo] = useState<UserRefundInfo | null>(null);
  const [currency, setCurrency] = useState(initial?.currency || 'KZT');
  const [amount, setAmount] = useState(
    initial?.amount != null ? String(initial.amount) : '',
  );
  const [reason, setReason] = useState(initial?.reason || '');
  const [reasonText, setReasonText] = useState(initial?.reasonText || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Оплаченная сумма по валютам — знаменатель «n / k». Валюту по умолчанию
  // берём из заказа, но только если её не задали снаружи (дозаполнение).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get(`/refunds/user/${userId}`);
        if (cancelled) return;
        setInfo(data);
        if (!initial?.currency && data?.currency) setCurrency(data.currency);
      } catch (err) {
        if (!cancelled) setError('Не удалось загрузить оплаченную сумму');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const paid = info?.paidByCurrency?.[currency] ?? 0;
  const entered = Number(amount);

  const submit = async () => {
    setError(null);
    if (!Number.isFinite(entered) || entered <= 0) {
      setError('Укажите сумму возврата');
      return;
    }
    if (!reason) {
      setError('Укажите причину возврата');
      return;
    }
    if (reason === 'other' && !reasonText.trim()) {
      setError('Опишите причину');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        userId,
        amount: Math.round(entered),
        currency,
        reason,
        reasonText: reason === 'other' ? reasonText.trim() : '',
      };
      const { data } = refundId
        ? await client.patch(`/refunds/${refundId}`, payload)
        : await client.post('/refunds', payload);
      onDone({ statusChanged: !!data?.statusChanged });
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rf-bg" onClick={() => !saving && onClose()}>
      <div className="rf-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="rf-title">{username}</h3>

        {error && <div className="rf-error">{error}</div>}

        <label className="rf-field">
          <span>Причина</span>
          <select
            className="rf-select"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          >
            <option value="">Выбрать</option>
            {REFUND_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>

        {reason === 'other' && (
          <label className="rf-field">
            <span>Опишите причину</span>
            <input
              className="rf-input"
              type="text"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Своя причина"
            />
          </label>
        )}

        <label className="rf-field">
          <span>Сумма возврата</span>
          <div className="rf-amount-row">
            <input
              className="rf-input rf-amount"
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
            />
            <span className="rf-paid">
              / {info ? fmtAmount(paid) : '…'} {currency}
            </span>
            <select
              className="rf-select rf-currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {REFUND_CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </label>

        <div className="rf-actions">
          <button
            type="button"
            className="rf-btn"
            disabled={saving}
            onClick={onClose}
          >
            Отмена
          </button>
          <button
            type="button"
            className="rf-btn rf-btn--primary"
            disabled={saving}
            onClick={submit}
          >
            {saving ? 'Сохранение…' : 'Сделать возврат'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefundModal;
